import * as React from 'react'
import { Dropdown, Menu, Modal, Space, Table } from 'antd'
import { TemplateItem, TemplateTableProps } from '../interface'
import { getFormatMessage, mergeTableColumns } from '@/utils/utils'
import { EllipsisOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { createFile } from '@/utils/file'
import { Guid } from '@/utils/guid'
import moment from 'moment'

const { confirm } = Modal

export const TemplateTable: React.FC<TemplateTableProps> = (
  props: TemplateTableProps
) => {
  const [item, setItem] = useState<TemplateItem>()
  const formatMessage = getFormatMessage()
  const reloadItem = async (item: TemplateItem) => {
    let newItem = item
    if (props.onItemLoad) {
      newItem = await props.onItemLoad(item.id!)
    }
    return newItem
  }

  const deleteItem = (item: TemplateItem) => {
    confirm({
      title: formatMessage({ id: 'app.modal.confirm.delete' }),
      icon: <ExclamationCircleOutlined />,
      okText: formatMessage({ id: 'app.modal.ok' }),
      cancelText: formatMessage({ id: 'app.modal.cancel' }),
      onOk() {
        if (props.onDelete) {
          props.onDelete(item)
        }
      }
    })
  }

  const exportItem = async (item: TemplateItem) => {
    item = await reloadItem(item)
    const json = JSON.stringify(item, undefined, 2)
    const time = moment().format('YYYYMMDDHHmmss')
    createFile(`se_template_${time}.json`, json)
  }

  const copyItem = async (item: TemplateItem) => {
    item = await reloadItem(item)
    if (props.onCopy) {
      props.onCopy(item)
    }
  }

  const editItem = async (item: TemplateItem) => {
    item = await reloadItem(item)
    if (props.onEdit) {
      props.onEdit(item)
    }
  }

  const applyItem = async (item: TemplateItem) => {
    item = await reloadItem(item)
    if (props.onApply) {
      props.onApply(item)
    }
  }

  const menu = (
    <Menu>
      <Menu.Item key={Guid.raw()}>
        <a onClick={() => copyItem(item || {})}>
          {formatMessage({ id: 'app.template.table.menu.copy' })}
        </a>
      </Menu.Item>
      <Menu.Item key={Guid.raw()}>
        <a onClick={() => deleteItem(item || {})}>
          {formatMessage({ id: 'app.template.table.menu.delete' })}
        </a>
      </Menu.Item>
      <Menu.Item key={Guid.raw()}>
        <a onClick={() => exportItem(item || {})}>
          {formatMessage({ id: 'app.template.table.menu.export' })}
        </a>
      </Menu.Item>
      <Menu.Item key={Guid.raw()}>
        <a onClick={() => editItem(item || {})}>
          {formatMessage({ id: 'app.template.table.menu.edit' })}
        </a>
      </Menu.Item>
    </Menu>
  )

  const defaultColumns = [
    {
      title: formatMessage({ id: 'app.template.table.index' }),
      key: 'app.template.table.index',
      render: (text: any, record: any, index: any) => index + 1
    },
    {
      title: formatMessage({ id: 'app.template.table.name' }),
      key: 'app.template.table.name',
      dataIndex: 'name'
    },
    {
      title: formatMessage({ id: 'app.template.table.applied' }),
      key: 'app.template.table.applied',
      render: (text: any, record: TemplateItem, index: any) =>
        formatMessage({ id: `app.template.table.applied.${record.applied}` })
    },
    {
      title: formatMessage({ id: 'app.template.table.plcCount' }),
      key: 'app.template.table.plcCount',
      render: (text: any, record: any, index: any) => {
        if (record.plcs) {
          if (Array.isArray(record.plcs)) {
            return record.plcs.length
          }
          return record.plcs
        }
        return ''
      }
    },
    {
      title: formatMessage({ id: 'app.template.table.variableCount' }),
      key: 'app.template.table.variableCount',
      render: (text: any, record: any, index: any) => {
        if (record.variables) {
          if (Array.isArray(record.variables)) {
            return record.variables.length
          }
          return record.variables
        }
        return ''
      }
    },
    {
      title: formatMessage({ id: 'app.template.table.transferProtocol' }),
      key: 'app.template.table.transferProtocol',
      render: (text: any, record: any, index: any) => {
        if (record.transfers) {
          let length = Array.isArray(record.transfers)
            ? record.transfers.length
            : record.transfers
          return length > 0 ? 'MQTT' : ''
        }
        return ''
      }
    },
    {
      title: formatMessage({ id: 'app.template.table.remark' }),
      key: 'app.template.table.remark',
      dataIndex: 'remark'
    },
    {
      title: formatMessage({ id: 'app.template.table.operation' }),
      key: 'app.template.table.operation',
      render: (text, record) => (
        <Space size='middle'>
          <a onClick={() => handleConfig(record)}>
            {formatMessage({ id: 'app.template.table.operation.config' })}
          </a>
          <a onClick={() => applyItem(record)}>
            {formatMessage({ id: 'app.template.table.operation.apply' })}
          </a>
          <Dropdown
            overlay={menu}
            onVisibleChange={(visible) => {
              if (visible) {
                setItem(record)
              }
            }}
          >
            <a
              className='ant-dropdown-link'
              onClick={(e) => e.preventDefault()}
            >
              <EllipsisOutlined />
            </a>
          </Dropdown>
        </Space>
      )
    }
  ]
  const columns = mergeTableColumns(defaultColumns, props.columns)
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      if (props.onSelect) {
        props.onSelect(selectedRows)
      }
    }
  }

  const handleConfig = async (item: TemplateItem) => {
    item = await reloadItem(item)
    if (props.onConfig) {
      props.onConfig(item)
    }
  }

  return (
    <Table
      rowKey='id'
      rowSelection={
        props.selectable
          ? {
              type: props.selectionType,
              ...rowSelection
            }
          : undefined
      }
      columns={columns}
      dataSource={props.data}
      pagination={props.pagination}
      size={props.size}
    />
  )
}
